import { RefFieldKeys } from "./handbookFormValues";

export type HandbookFormValues = Record<string, any>;

export type PayloadOptions = RefFieldKeys & {
  initialId?: number | null;
  isAllServices?: boolean;
  isAllHandbooks?: boolean;
  showAlias?: boolean;
  showAddress?: boolean;
  showDescriptionRu?: boolean;
  showDescriptionKy?: boolean;
  showBik?: boolean;
  showDivision?: boolean;
  showOrder?: boolean;
  showThresholdPerM2?: boolean;
  showDataSourceStatus?: boolean;
  showCategory?: boolean;
  showCrop?: boolean;
  showBank?: boolean;
  showRegion?: boolean;
  showType?: boolean;
  showUnitMeasurement?: boolean;
};

/**
 * Собирает тело запроса из значений формы. В payload попадают только те поля,
 * которые у этого справочника вообще есть, — иначе бэкенд получит лишние ключи.
 */
export function buildHandbookPayload(
  v: HandbookFormValues,
  o: PayloadOptions,
): Record<string, any> {
  const payload: Record<string, any> = {
    id: o.initialId ?? undefined,
    nameRu: v.nameRu?.trim(),
    nameKy: v.nameKy?.trim(),
  };

  const alias = (v.alias ?? "").trim();

  if ((o.isAllServices && v.code) || (o.isAllHandbooks && v.code)) {
    payload.code = v.code?.trim();
  }

  if (o.showAlias && alias !== "") payload.alias = alias;
  if (o.showAddress && v.address !== null) payload.address = v.address;

  if (o.showDescriptionRu && v.descriptionRu !== null) {
    payload.descriptionRu = v.descriptionRu;
  }
  if (o.showDescriptionKy && v.descriptionKy !== null) {
    payload.descriptionKy = v.descriptionKy;
  }

  if (o.showBik && v.bik !== null) payload.bik = v.bik;

  if (o.showDivision && v.division) payload.division = v.division;
  if (o.showOrder && v.order) payload.order = v.order;

  // Поле ввода отдаёт строку; ноль — валидный порог, пустое поле — сброс значения.
  if (o.showThresholdPerM2) {
    const raw = v.thresholdPerM2;
    payload.thresholdPerM2 =
      raw === null || raw === undefined || String(raw).trim() === ""
        ? null
        : Number(raw);
  }
  if (o.showDataSourceStatus && v.dataSourceStatus) {
    payload.dataSourceStatus = v.dataSourceStatus;
  }

  // Ссылка уходит в поле `<имя>Id`. Ноль — валидный идентификатор,
  // поэтому проверяем не на truthy.
  const refs = [
    { show: o.showCategory, fieldKey: o.category, value: v.categoryId },
    { show: o.showCrop, fieldKey: o.crop, value: v.cropId },
    { show: o.showBank, fieldKey: o.bank, value: v.bankId },
    { show: o.showType, fieldKey: o.type, value: v.typeId },
    {
      show: o.showUnitMeasurement,
      fieldKey: o.unitMeasurement,
      value: v.unitMeasurementId,
    },
  ];

  for (const { show, fieldKey, value } of refs) {
    if (show && fieldKey && (value === 0 || value)) {
      payload[`${fieldKey}Id`] = value;
    }
  }

  // Регион редактируется только вместе с БИК — у остальных справочников его нет.
  if (o.showRegion && o.showBik) payload.regionId = v.regionId ?? null;

  if (o.isAllHandbooks) {
    const codes = Array.isArray(v.serviceCodes)
      ? v.serviceCodes
      : v.serviceCodes
        ? [v.serviceCodes]
        : [];
    payload.serviceCodes = codes;
  }

  return payload;
}
